
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { TableItemFormatter } from '../../elements/charts/helpers/tableItemFormatter';
import gdbx from '@/store/modules/gdbx';
import { COL, getTableName } from '@/worker/db/sqlHelpers';

@Component({
  components: {
    TableFilterable,
  },
})
export default class SalesToDeliver extends FilterWidget {
  public filterIds: Array<
    | 'date'
    | 'dateAsOf'
    | 'dateRange'
    | 'stockItems'
    | 'agents'
    | 'customers'
    | 'suppliers'
  > = [];

  public tableItems: any[] = [];
  public tableFields: any[] = [];
  public detailFields: any[] = [];

  public cachedRefHead: any = null;

  public infoModal = {
    id: 'info-modal2',
    index: 0,
    title: '',
    extras: '',
    amount: '',
    docDate: 0,
    os_amount: '',
    items: [] as any[],
  };

  public info(item, extras, index, title, amount, osAmount, link) {
    this.infoModal.title = title + ' ' + '( ' + item.items[0].customer + ' )';
    this.infoModal.extras = extras;
    this.infoModal.index = index;
    this.infoModal.amount = String(Math.abs(amount));
    this.infoModal.os_amount = (osAmount);
    this.infoModal.docDate = item.items[0].docDate;
    this.infoModal.items = item.items;
    this.$root.$emit('bv::show::modal', this.infoModal.id, link);
  }

  public resetInfoModal() {
    this.infoModal.title = '';
    this.infoModal.extras = '';
    this.infoModal.items = [];
  }

  public get permissionIds(): PermissionsGroup[] {
    return ['sales'];
  }

  public get exportFileName() {
    return 'To Deliver' + '_' + this.monthName + ' ' + this.yearName;
  }

  public get currentMonth() {
    return moment(this.selectedAsOfDate).month();
  }

  public get currentYear() {
    return moment(this.selectedAsOfDate).year();
  }

  public get monthName() {
    return moment().month(this.currentMonth).format('MMM').toUpperCase();
  }

  public get yearName() {
    return moment().year(this.currentYear).format('YY').toUpperCase();
  }

  public get expensiveHook() {
    const { selectedDateRange } = this;
    return JSON.stringify([selectedDateRange]);
  }

  public async loadDetailForDoc(docNo: string): Promise<any[]> {
    if (!this.cachedRefHead) {
      this.cachedRefHead = await this._ensureTablesLoaded(['salesOrder'] as any[]);
    }

    const tableName = getTableName('salesOrder');
    const dateFilter = `TRY_CAST("DELIVERYDATE" AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
    const escaped = docNo.replace(/'/g, '\'\'');
    const lineData = await this.cachedRefHead.rawQuery(`
        SELECT TRY_CAST("SEQ" AS INTEGER) as seq,
            ${COL.CODE} as code,
            ${COL.ITEMCODE} as itemCode,
            ${COL.AGENT} as agent,
            TRY_CAST(${COL.DOCDATE} AS BIGINT) as docDate,
            TRY_CAST("DELIVERYDATE" AS BIGINT) as deliveryDate,
            TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
            TRY_CAST(${COL.QUANTITY} AS DOUBLE) as quantity,
            TRY_CAST("OSQTY" AS DOUBLE) as osQty
        FROM ${tableName}
        WHERE ${dateFilter} AND ${COL.DOCNO} = '${escaped}'
    `);

    return lineData
        .filter((row: any) => (Number(row.osQty) || 0) > 0)
        .map((row: any) => {
          const amt = Number(row.amount) || 0;
          const qty = Number(row.quantity) || 0;
          const osQty = Number(row.osQty) || 0;
          return {
            'seq.': Number(row.seq) || 0,
            'customer': gdbx.customerNames[String(row.code)],
            'docDate': Number(row.docDate),
            'item': gdbx.stockItemNames[String(row.itemCode)],
            'item_code': String(row.itemCode),
            'expected_delivery': osQty
                ? moment(Number(row.deliveryDate)).format('DD/MM/YYYY')
                : null,
            'unit_price': qty ? amt / qty : 0,
            'amount': amt,
            'os_amount': osQty === 0 || qty === 0
                ? 0 : (amt / qty) * osQty,
            'quantity': qty,
            'os_quantity': osQty,
            '_rowVariant': osQty > 0 ? 'top-five' : '',
            'agent': String(row.agent),
          };
        });
  }

  public async expensiveCalc() {
    const jobId = this.currentJobId;
    this.generateLoadingText(0.2, 0.4);

    const queryHead = await this._ensureTablesLoaded(['salesOrder'] as any[]);
    this.checkInterrupted(jobId);
    this.generateLoadingText(0.4, 0.9);

    const tableName = getTableName('salesOrder');
    const dateFilter = `TRY_CAST("DELIVERYDATE" AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
    this.cachedRefHead = queryHead;

    const summaryData = await queryHead.rawQuery(`
        SELECT ${COL.DOCNO} as docNo,
            MIN(TRY_CAST(${COL.DOCDATE} AS BIGINT)) as docDate,
            MIN(${COL.CODE}) as code,
            MIN(${COL.AGENT}) as agent,
            MIN(TRY_CAST("DELIVERYDATE" AS BIGINT)) as deliveryDate,
            COUNT(CASE WHEN TRY_CAST("OSQTY" AS DOUBLE) > 0 THEN 1 END) as osItemCount,
            SUM(CASE
                WHEN TRY_CAST(${COL.QUANTITY} AS DOUBLE) > 0
                    AND TRY_CAST("OSQTY" AS DOUBLE) > 0
                THEN (TRY_CAST(${COL.AMOUNT} AS DOUBLE)
                    / TRY_CAST(${COL.QUANTITY} AS DOUBLE))
                    * TRY_CAST("OSQTY" AS DOUBLE)
                ELSE 0 END) as osAmount
        FROM ${tableName}
        WHERE ${dateFilter}
        GROUP BY ${COL.DOCNO}
        HAVING COUNT(CASE WHEN TRY_CAST("OSQTY" AS DOUBLE) > 0 THEN 1 END) > 0
    `);

    const now = moment().valueOf();

    const result = summaryData.map((row: any) => {
        const docNo = String(row.docNo);
        const code = String(row.code);
        const docDate = Number(row.docDate);
        const deliveryDate = Number(row.deliveryDate);
        const osAmount = Number(row.osAmount) || 0;

        return {
            'so_no': docNo,
            'so_date': moment(docDate).format('DD/MM/YYYY'),
            'expected_delivery': moment(deliveryDate).format('DD/MM/YYYY'),
            'numberDate': deliveryDate,
            'customer': gdbx.customerNames[code] || '',
            'late_(days)': Math.ceil(
                moment.duration(now - deliveryDate).asDays(),
            ),
            'os_item': Number(row.osItemCount) || 0,
            'os_amount': osAmount,
            'agent': String(row.agent),
            'extras': `- ${moment(docDate).format('DD/MM/YYYY')}
						- ${gdbx.customerNames[code]}`,
            'items': [],
        };
    });
    this.generateLoadingText(0.95);

    this.tableFields = [
      {
        key: 'late_(days)',
        sortable: false,
        numeric: true,
      },
      {
        key: 'expected_delivery',
        sortable: false,
      },
      {
        key: 'customer',
        sortable: false,
      },
      {
        key: 'os_item',
        sortable: false,
        formatter: TableItemFormatter.basicInteger,
      },
      {
        key: 'os_amount',
        sortable: false,
        formatter: TableItemFormatter.currencySplit,
      },
      {
        key: 'agent',
        sortable: false,
      },
      {
        key: 'so_date',
        sortable: false,
      },
      {
        key: 'so_no',
        sortable: false,
      },
      ];

    this.detailFields = [
      { key: 'seq.', label: 'Seq', sortable: true },
      {
        key: 'item_code',
        sortable: true,
      },
      {
        key: 'item',
        sortable: true,
      },
      {
        key: 'expected_delivery',
        sortable: true,
      },
      {
        key: 'os_quantity',
        label: 'O/S Qty',
        sortable: true,
        formatter: TableItemFormatter.basicInteger,
      },
      {
        key: 'quantity',
        label: 'Qty',
        sortable: true,
        formatter: TableItemFormatter.basicInteger,
      },
      {
        key: 'unit_price',
        sortable: true,
        formatter: TableItemFormatter.currencySplit,
      },
      {
        key: 'os_amount',
        label: 'O/S Amt',
        sortable: true,
        formatter: TableItemFormatter.currencySplit,
      },
      {
        key: 'amount',
        label: 'Amt',
        sortable: true,
        formatter: TableItemFormatter.currencySplit,
      },


    ];

    this.checkInterrupted(jobId);

    this.tableItems = result.filter((a) => {
        return a.os_amount > 0;
    }).sort((a, b) => b['late_(days)'] - a['late_(days)']);

    this.saveHistory('tableItems', 'tableFields', 'detailFields');
  }
}
